/**
 * Kantar–DİA mutabakatı: `CanliTartim` (kantar tartımları) ile `Rpr183` (DİA
 * irsaliye kalemleri) tartı bazında eşleştirilir ve kontrol edilir. Kurallar
 * kullanıcı tarafından tanımlandı (2026-09-28; H-L 2026-10-17, 2026-11-03; bkz. docs/HANDOFF.md); burada
 * icat edilmiş bir kural yoktur. Saf modül: Cube'u tanımaz, satırları eşlenmiş alır.
 */

import { formatCubeDateTime, formatDateOnly, formatDuration } from '@/lib/format'
import { isTimeOfDay } from '@/lib/time-of-day'

export const KANTAR_VIEW = 'CanliTartim'

export const ISSUE_CODES = [
  'H-A',
  'H-B',
  'H-C',
  'H-D',
  'H-E',
  'H-F',
  'U-G',
  'U-H',
  'U-I',
  'U-J',
  'H-K',
  'H-L',
  'U-M',
] as const
export type IssueCode = (typeof ISSUE_CODES)[number]

/** U-H iki alanı ayrı bildirir; detay paneli farkı doğru satıra bu notla bağlar. */
export const U_H_NOTE = { driver: 'Şoför', carrier: 'Nakliyeci' } as const

/**
 * Tartım ile irsaliye arasında beklenen süre; dolmadan "İrsaliye bekleniyor". DİA verisi
 * 15 dk'da bir çekiliyor (ölçüldü 2026-10-21): iki senkron turu (kullanıcı kararı, 30 dk).
 */
export const IRSALIYE_WAIT_MS = 30 * 60_000
/** Bu süreden uzun sahada kalan aracın süresi kırmızı gösterilir. */
export const LONG_ONSITE_MS = 2 * 60 * 60_000
/**
 * Girişten bu kadar sonra hâlâ Sahada (çıkış tartımı yok) ise hata, H-L. Kullanıcı kararı:
 * 2026-10-17'de 3 gün, 2026-11-03'te 24 saat (son 180 günde tamamlanan 24.673 tartının en
 * uzunu 13,8 sa sürdü; 24 saati aşan hiç yok).
 */
export const STALE_ONSITE_MS = 24 * 60 * 60_000
/**
 * Aynı kantarda aynı plaka sonradan tartılıp çıktıysa, bu süreden uzun açık Sahada kaydı da
 * H-L (kullanıcı kararı, 2026-11-03). Süre şartı: aynı plakayla üst üste açık iki tartı gerçekten
 * oluyor (180 günde 13 kez, muhtemelen römork); en uzunu 5 sa 20 dk açık kaldı. 6 saatle 180 günde
 * yanlış alarm yok.
 */
export const REWEIGHED_OPEN_MS = 6 * 60 * 60_000
const DAY_MS = 24 * 60 * 60_000
/**
 * Canlı güncellemenin açık kaldığı en uzun aralık (gün, uçlar dahil). Daha uzun aralıkta Cube
 * aboneliği her senkronda (~1,5 dk) on binlerce satırı yeniden gönderiyor, Cube'un belleği
 * doluyordu (yerel ölçüm 2026-10-19: 45–108 MB, Cube yeniden başladı).
 */
export const LIVE_MAX_DAYS = 7
/**
 * Kantar günü ile DİA irsaliye günü arasında uyarısız fark (kullanıcı kararı, 2026-10-18):
 * 23:55'te çıkan aracın irsaliyesi ertesi güne sarkabilir.
 */
export const DATE_TOLERANCE_DAYS = 1

/**
 * Yalnız bu depoların DİA satırları karşılaştırılır (kullanıcı kararı, 2026-10-18).
 * Dışarıda kalan "Terzili Ticari": ticari satış, çoğunda tartı no yok.
 */
export const RECONCILE_DEPOTS = ['TERZİLİ', 'Ladik Taşocağı Depo', 'Çamlıyazı Depo'] as const

const OWN_TRANSPORT = 'Nakliye Kendine Ait'
/** Şirketin kendi aracı: satışta HZMT olabilir, Mal Alım satırı olmamalı (kullanıcı kararı). */
const COMPANY_TRANSPORT = 'SAMSUN BETONTAŞ MADENCİLİK ANONİM ŞİRKETİ'

/** Kantar -> DİA ön eki ve firma kodu. Çamlıyazı tanımlı değil (ayrıca raporlanır). */
const EXPECTED_PREFIX: Record<string, { prefix: string; firm: number }> = {
  Arslantaş: { prefix: 'L', firm: 9 },
  Terzili: { prefix: 'B', firm: 12 },
}

export interface KantarRow {
  kantarAdi: string
  tartino: number
  surecDurumu: string | null
  /** `YYYY-MM-DD…` takvim günü. */
  tarih: string | null
  /** Cube'un offset'siz İstanbul duvar saati dizeleri. */
  giris: string | null
  cikis: string | null
  girisKg: number | null
  cikisKg: number | null
  cariKod: string | null
  cariAdi: string | null
  stokKod: string | null
  plaka: string | null
  sofor: string | null
  nakliyeci: string | null
  siparisNo: string | null
}

/** Kantar programının DİA aktarım sonucu (`IrsaliyeAktarim`), tartı başına son kayıt. */
export interface AktarimRow {
  kantarAdi: string
  tartino: number
  /** 1 = DİA'ya aktarıldı, 0 = aktarılmadı. */
  aktarildi: number | null
  /** Başarısız aktarımda DİA'nın yanıtı (`{"code": "501", "msg": "…"}`). */
  aktarimHatasi: string | null
}

/** DİA hata yanıtından okunur mesaj: JSON `msg`, HTML ve baştaki `*` temizlenir. */
export function aktarimHataMesaji(raw: string | null): string | null {
  if (!raw?.trim()) return null
  let message = raw
  try {
    const parsed = JSON.parse(raw) as { msg?: unknown }
    if (typeof parsed.msg === 'string') message = parsed.msg
  } catch {
    // JSON değilse olduğu gibi gösterilir.
  }
  return (
    message
      .replace(/<[^>]*>/g, '')
      .replace(/^[\s*]+/, '')
      .replace(/\s*\(\)\s*$/, '')
      .trim() || null
  )
}

export interface DiaRow {
  /** DİA kalem anahtarı; satır sırası (eşleştirme ilk MLZM satırını alır) bununla sabit. */
  diaKalemKey?: number | null
  kantarId: string
  tartiNo: number
  tartiOnek: string | null
  firmaKodu: number | null
  tarih: string | null
  irsaliyeNo: string | null
  irsaliyeTuru: string | null
  kalemTuru: string | null
  cariKod: string | null
  cariUnvan: string | null
  plaka: string | null
  surucu: string | null
  nakliyeciUnvan: string | null
  siparisBelgeNo: string | null
  stokKod: string | null
  /** Ton. */
  miktar: number | null
}

export interface Issue {
  code: IssueCode
  /** Alt açıklama (hangi alan / neden). */
  note?: string
  kantar?: string
  dia?: string
  diff?: string
}

export type RecordStatus = 'error' | 'warning' | 'onsite' | 'waiting' | 'ok' | 'deleted'

export interface ReconcileRecord {
  key: string
  kantarAdi: string
  tartino: number
  plaka: string | null
  cari: string | null
  /** Kantar takvim günü; kantar kaydı yoksa ilk DİA satırının tarihi. */
  tarih: string | null
  kantarTon: number | null
  diaTon: number | null
  status: RecordStatus
  issues: Issue[]
  giris: string | null
  /** Sahada (durum `onsite`) kaydında girişten bu yana geçen süre; giriş bilinmiyorsa `null`. */
  onsiteMs: number | null
  /** Detay paneli için ham kayıtlar. */
  kantar: KantarRow | null
  lines: DiaRow[]
  /** Elle düzeltme (`applyOverrides`); varsa `status` düzeltilmiş, `computedStatus` kuralların. */
  override?: RecordOverride
  computedStatus?: RecordStatus
}

/** Elle verilebilen durumlar (backend `OverrideStatus`). */
export const OVERRIDE_STATUSES = ['ok', 'warning', 'error'] as const
export type OverrideStatus = (typeof OVERRIDE_STATUSES)[number]

export interface RecordOverride {
  /** `null` = durum değişmez, yalnız açıklama. */
  status: OverrideStatus | null
  note: string
  updatedByName: string | null
  updatedAt: string
}

export interface OverrideRow extends RecordOverride {
  kantarAdi: string
  tartino: number
}

export interface PrefixMismatch {
  kantarAdi: string
  tartino: number
  expected: string
  actual: string
}

export interface ReconcileResult {
  records: ReconcileRecord[]
  prefixMismatches: PrefixMismatch[]
  /** Ön eki tanımsız kantarlarda DİA'da görülen ön ek/firma değerleri (ör. Çamlıyazı). */
  unknownPrefixes: { kantarAdi: string; values: string[] }[]
}

const STATUS_RANK: Record<RecordStatus, number> = {
  error: 0,
  warning: 1,
  onsite: 2,
  waiting: 3,
  ok: 4,
  deleted: 5,
}

const keyOf = (kantar: string, tartino: number) => `${kantar}:${tartino}`
const text = (value: string | null) => value ?? ''
/** Kod alanları metindir, olduğu gibi karşılaştırılır (`01.58851` sayı değil). */
const sameCode = (a: string | null, b: string | null) => text(a) === text(b)
/**
 * Tekrarlayan dizelerin (cari, nakliyeci, plaka) dönüşümü önbellekli: on binlerce kayıtta
 * aynı birkaç yüz ad için `toLocaleUpperCase`/`normalize` tekrar tekrar çalışıyordu.
 * Önbellek dolarsa boşaltılır (bellek sınırlı kalsın).
 */
function memoize(fn: (value: string) => string, max = 50_000) {
  const cache = new Map<string, string>()
  return (value: string | null) => {
    const key = text(value)
    let result = cache.get(key)
    if (result === undefined) {
      if (cache.size >= max) cache.clear()
      result = fn(key)
      cache.set(key, result)
    }
    return result
  }
}
/** Büyük/küçük harf, Türkçe karakter ve boşluk farkı yok sayılır. */
export const normalizeName = memoize((value) =>
  value.toLocaleUpperCase('tr').normalize('NFD').replace(/\p{M}/gu, '').replace(/\s+/g, ' ').trim(),
)
const sameName = (a: string | null, b: string | null) => normalizeName(a) === normalizeName(b)
export const normalizePlate = memoize((value) => value.toLocaleUpperCase('tr').replace(/\s+/g, ''))
/** Tek Türkçe karşılaştırıcı: `localeCompare(…, 'tr')` her çağrıda yenisini kurar (yavaş). */
export const compareTr = new Intl.Collator('tr').compare
const day = (value: string | null) => value?.slice(0, 10) ?? null

/** Ton -> tam kg (3 ondalık hassasiyet). */
const toKg = (ton: number | null) => (ton === null ? null : Math.round(ton * 1000))

/** Kantar neti: `cikis_kg - giris_kg` (brüt/dara etiketleri ve `net_*` kullanılmaz). */
const kantarKg = (row: KantarRow) =>
  row.cikisKg === null || row.girisKg === null ? null : Math.round(row.cikisKg - row.girisKg)

/** Offset'siz duvar saati -> karşılaştırılabilir ms (metinden; `new Date(str)` yok). */
export function wallClockMs(value: string | null): number | null {
  const match = value && /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/.exec(value)
  if (!match) return null
  const [, y, mo, d, h, mi, s] = match.map(Number) as number[]
  return Date.UTC(y!, mo! - 1, d!, h, mi, s)
}

/** İki takvim günü (`YYYY-MM-DD…`) arasındaki gün farkı, mutlak. */
function dayGap(a: string, b: string): number | null {
  const ams = wallClockMs(`${a.slice(0, 10)}T00:00:00`)
  const bms = wallClockMs(`${b.slice(0, 10)}T00:00:00`)
  return ams === null || bms === null ? null : Math.round(Math.abs(ams - bms) / DAY_MS)
}

/** `YYYY-MM-DD` gününü `days` kaydırır (UTC alanlarıyla; saat dilimi kaymaz). */
export function shiftDay(value: string, days: number): string {
  const ms = wallClockMs(`${value}T00:00:00`)
  return ms === null ? value : new Date(ms + days * DAY_MS).toISOString().slice(0, 10)
}

/**
 * DİA sorgusunun başlangıcı: aralık başından tolerans kadar önce, aralığın ilk gününün
 * tartısına bir gün önce tarihlenmiş irsaliye de eşleşsin.
 */
/** Aralık canlı izlenir mi: bugünü kapsıyor ve en fazla `LIVE_MAX_DAYS` gün. */
export function isLiveRange(from: string | undefined, to: string | undefined, today: string) {
  if (!from || (to && to < today)) return false
  const gap = dayGap(from, to && to < today ? to : today)
  return gap !== null && gap < LIVE_MAX_DAYS
}

export const diaFromDay = (from?: string) =>
  from ? shiftDay(from, -DATE_TOLERANCE_DAYS) : undefined

export const formatTon = (kg: number) =>
  (kg / 1000).toLocaleString('tr-TR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })

function tonDiff(kantar: number, dia: number): string {
  const diff = dia - kantar
  const sign = diff > 0 ? '+' : ''
  return `${sign}${diff.toLocaleString('tr-TR')} kg (${sign}${formatTon(diff)} ton)`
}

function inRange(value: string | null, from?: string, to?: string) {
  const d = day(value)
  if (d === null) return false
  return (!from || d >= from) && (!to || d <= to)
}

function transportIssues(kantar: KantarRow, lines: DiaRow[], mlzmKg: number | null): Issue[] {
  const issues: Issue[] = []
  const transport = lines.filter(
    (line) => line.kalemTuru === 'HZMT' || line.irsaliyeTuru === 'Mal Alım',
  )
  const purchase = transport.filter((line) => line.irsaliyeTuru === 'Mal Alım')

  if (mlzmKg !== null) {
    for (const line of transport) {
      const kg = toKg(line.miktar)
      if (kg !== mlzmKg) {
        issues.push({
          code: 'U-J',
          note: `${text(line.irsaliyeTuru)} / ${text(line.kalemTuru)} miktarı MLZM'den farklı`,
          kantar: formatTon(mlzmKg),
          dia: kg === null ? undefined : formatTon(kg),
          diff: kg === null ? undefined : tonDiff(mlzmKg, kg),
        })
      }
    }
  }

  const carrier = kantar.nakliyeci
  if (!text(carrier).trim()) return issues // nakliyeci bilinmiyor: kontrol edilemez
  if (sameName(carrier, OWN_TRANSPORT)) {
    if (transport.length > 0) {
      issues.push({
        code: 'U-J',
        note: 'Nakliye kendine ait ama nakliye satırı var',
        kantar: text(carrier),
        dia: `${transport.length} satır`,
      })
    }
  } else if (sameName(carrier, COMPANY_TRANSPORT)) {
    if (purchase.length > 0) {
      issues.push({
        code: 'U-J',
        note: 'Şirket aracı ama Mal Alım satırı var',
        kantar: text(carrier),
        dia: purchase.map((line) => text(line.cariUnvan)).join(', '),
      })
    }
  } else if (purchase.length === 0) {
    // Ayrı kod: "nakliyeci Mal Alım'ı atılmış mı" tek tıkla süzülsün (kullanıcı isteği, 2026-10-23).
    issues.push({ code: 'U-M', kantar: text(carrier), dia: 'yok' })
  } else {
    for (const line of purchase) {
      if (!sameName(line.cariUnvan, carrier)) {
        issues.push({
          code: 'U-J',
          note: 'Mal Alım carisi nakliyeciyle uyumsuz',
          kantar: text(carrier),
          dia: text(line.cariUnvan),
        })
      }
    }
  }
  return issues
}

function compareMlzm(kantar: KantarRow, mlzm: DiaRow): Issue[] {
  const issues: Issue[] = []
  const aKg = kantarKg(kantar)
  const bKg = toKg(mlzm.miktar)
  if (aKg === null || bKg === null || aKg !== bKg) {
    issues.push({
      code: 'H-C',
      kantar: aKg === null ? undefined : formatTon(aKg),
      dia: bKg === null ? undefined : formatTon(bKg),
      diff: aKg === null || bKg === null ? undefined : tonDiff(aKg, bKg),
    })
  }
  if (!sameCode(kantar.cariKod, mlzm.cariKod)) {
    issues.push({ code: 'H-D', kantar: text(kantar.cariKod), dia: text(mlzm.cariKod) })
  }
  if (!sameCode(kantar.stokKod, mlzm.stokKod)) {
    issues.push({ code: 'H-E', kantar: text(kantar.stokKod), dia: text(mlzm.stokKod) })
  }
  if (normalizePlate(kantar.plaka) !== normalizePlate(mlzm.plaka)) {
    issues.push({
      code: 'H-F',
      kantar: normalizePlate(kantar.plaka),
      dia: normalizePlate(mlzm.plaka),
    })
  }
  if (!sameCode(kantar.siparisNo, mlzm.siparisBelgeNo)) {
    issues.push({ code: 'U-G', kantar: text(kantar.siparisNo), dia: text(mlzm.siparisBelgeNo) })
  }
  if (!sameName(kantar.sofor, mlzm.surucu)) {
    issues.push({
      code: 'U-H',
      note: U_H_NOTE.driver,
      kantar: text(kantar.sofor),
      dia: text(mlzm.surucu),
    })
  }
  const ownWithoutCarrier =
    sameName(kantar.nakliyeci, OWN_TRANSPORT) && !text(mlzm.nakliyeciUnvan).trim()
  if (!ownWithoutCarrier && !sameName(kantar.nakliyeci, mlzm.nakliyeciUnvan)) {
    issues.push({
      code: 'U-H',
      note: U_H_NOTE.carrier,
      kantar: text(kantar.nakliyeci),
      dia: text(mlzm.nakliyeciUnvan),
    })
  }
  const gap = kantar.tarih && mlzm.tarih ? dayGap(kantar.tarih, mlzm.tarih) : null
  if (day(kantar.tarih) !== day(mlzm.tarih) && (gap === null || gap > DATE_TOLERANCE_DAYS)) {
    issues.push({
      code: 'U-I',
      kantar: formatDateOnly(kantar.tarih),
      dia: formatDateOnly(mlzm.tarih),
    })
  }
  return issues
}

/** Plaka karşılaştırması: boşluk ve harf büyüklüğü farkı aynı araç sayılır. */
const plateKey = (plaka: string | null) => text(plaka).replace(/\s/g, '').toLocaleUpperCase('tr-TR')

/** Sahada kaydının ardından aynı kantarda aynı plakayla açılıp çıkış yapmış ilk tartı. */
function laterExit(
  kantar: KantarRow,
  girisMs: number,
  nowMs: number,
  byPlate: Map<string, KantarRow[]>,
): KantarRow | undefined {
  const plate = plateKey(kantar.plaka)
  if (!plate) return undefined
  let first: { row: KantarRow; cikisMs: number } | undefined
  for (const row of byPlate.get(`${kantar.kantarAdi}|${plate}`) ?? []) {
    const rowGiris = wallClockMs(row.giris)
    const cikisMs = wallClockMs(row.cikis)
    if (row.tartino === kantar.tartino || row.surecDurumu === 'Silindi') continue
    if (rowGiris === null || rowGiris <= girisMs || cikisMs === null || cikisMs > nowMs) continue
    if (!first || cikisMs < first.cikisMs) first = { row, cikisMs }
  }
  return first?.row
}

/**
 * H-L: kantarda Sahada, çıkış tartımı yok ve ya girişten 24 saatten fazla geçmiş ya da araç
 * (aynı kantar + plaka) sonradan yeniden tartılıp çıkmış ve kayıt 6 saatten uzun açık.
 * Giriş zamanı bilinmiyorsa süre ölçülemez; işaretlenmez.
 */
function staleOnsiteIssue(
  kantar: KantarRow | undefined,
  girisMs: number | null,
  nowMs: number,
  byPlate: Map<string, KantarRow[]>,
): Issue | undefined {
  if (kantar?.surecDurumu !== 'Sahada' || girisMs === null) return undefined
  const elapsed = nowMs - girisMs
  const issue = {
    code: 'H-L' as const,
    kantar: `Giriş ${formatCubeDateTime(kantar.giris)}`,
    diff: formatDuration(elapsed),
  }
  if (elapsed > STALE_ONSITE_MS) {
    return { ...issue, note: 'Çıkış tartımı yapılmamış, 24 saati aştı' }
  }
  const later = elapsed > REWEIGHED_OPEN_MS && laterExit(kantar, girisMs, nowMs, byPlate)
  if (!later) return undefined
  return {
    ...issue,
    note: `Çıkış tartımı yapılmamış; araç sonra yeniden tartıldı (${later.tartino}, çıkış ${formatCubeDateTime(later.cikis)})`,
  }
}

/**
 * Yeniden tartım kuralı (`REWEIGHED_OPEN_MS`) için aranacak araçlar: kantar -> plakalar ve en
 * erken giriş günü. Yalnız 24 saatten genç Sahada kayıtları; daha eskisi süre kuralıyla zaten H-L.
 */
export function reweighCandidates(
  kantar: KantarRow[],
  nowMs: number,
): Map<string, { plates: string[]; from: string }> {
  const byKantar = new Map<string, { plates: Set<string>; from: string }>()
  for (const row of kantar) {
    const girisMs = wallClockMs(row.giris)
    if (row.surecDurumu !== 'Sahada' || girisMs === null || !text(row.plaka).trim()) continue
    if (nowMs - girisMs > STALE_ONSITE_MS) continue
    const day = row.giris!.slice(0, 10)
    const entry = byKantar.get(row.kantarAdi) ?? { plates: new Set<string>(), from: day }
    entry.plates.add(row.plaka!)
    if (day < entry.from) entry.from = day
    byKantar.set(row.kantarAdi, entry)
  }
  return new Map(
    [...byKantar]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([kantarAdi, { plates, from }]) => [kantarAdi, { plates: [...plates].sort(), from }]),
  )
}

function statusOf(issues: Issue[]): RecordStatus {
  if (issues.some((issue) => issue.code.startsWith('H-'))) return 'error'
  return issues.length > 0 ? 'warning' : 'ok'
}

/** Ardışık tartı no'lar arasında bu kadar boşluk tek parçada birleşir (sorgu kısa kalsın). */
const RUN_GAP = 20

/**
 * Geçmiş aralıkta DİA toplu sorgusu `[from-1, to+1]` ile sınırlanır; kapsamdaki tartılara
 * sonradan kesilmiş satırlar ayrı, küçük bir sorguyla alınır (`diaLateQuery`). Önceden geçmiş
 * bir ay için bugüne kadarki bütün DİA (43 bin satır) çekiliyordu (ölçüm 2026-10-23).
 *
 * Kapsamdaki tartılar `reconcile`'in kuralıyla aynı: kantar günü (Sahada'da giriş) aralıkta
 * olan kantar tartımı, girişi bilinmeyen Sahada, ya da aralıkta DİA satırı olan tartı.
 * Kantar başına tartı no'lar sıralı parçalara (`[ilk, son]`, aralarında en çok `RUN_GAP`
 * boşluk) toplanır; parçalar kapsamdaki her tartıyı içerir, fazlası zararsızdır.
 * Gerçek veriyle eşitlik denendi (Haziran, Eylül, 1–15 Ekim: kayıtlar birebir aynı).
 */
export function lateTartiRuns(
  kantar: KantarRow[],
  dia: DiaRow[],
  from?: string,
  to?: string,
): Map<string, [number, number][]> {
  const numbers = new Map<string, Set<number>>()
  const add = (kantarAdi: string | null, tartino: number | null) => {
    if (!kantarAdi || tartino === null || !Number.isFinite(tartino)) return
    const set = numbers.get(kantarAdi) ?? new Set<number>()
    set.add(tartino)
    numbers.set(kantarAdi, set)
  }
  for (const row of kantar) {
    const onsite = row.surecDurumu === 'Sahada'
    const kantarDay = onsite ? row.giris : row.tarih
    if (kantarDay ? inRange(kantarDay, from, to) : onsite) add(row.kantarAdi, row.tartino)
  }
  for (const row of dia) if (inRange(row.tarih, from, to)) add(row.kantarId, row.tartiNo)

  const runs = new Map<string, [number, number][]>()
  for (const [kantarAdi, set] of [...numbers].sort(([x], [y]) => x.localeCompare(y))) {
    const list: [number, number][] = []
    for (const n of [...set].sort((x, y) => x - y)) {
      const last = list.at(-1)
      if (last && n - last[1] <= RUN_GAP) last[1] = n
      else list.push([n, n])
    }
    runs.set(kantarAdi, list)
  }
  return runs
}

/**
 * DİA aktarım kaydı istenecek tartılar: kantar -> sıralı tartı no'lar. Aktarım kaydı yalnız
 * DİA satırı olmayan kantar tartımında kullanılır (`reconcile`, H-A ve "İrsaliye bekleniyor");
 * Sahada ve Silindi dalları ona bakmaz. Önceden aralıktaki bütün tartıların kaydı çekiliyordu
 * (Ocak–bugün: 34 bin satır, ilk sorgu turunu yavaşlatıyordu; ölçüm 2026-10-23). Burada `dia`
 * aralığın satırları; aralık sonrasında satırı olan tartı da aday olur (fazlası zararsız).
 */
export function aktarimCandidates(kantar: KantarRow[], dia: DiaRow[]): Map<string, number[]> {
  const withLines = new Set(dia.map((row) => keyOf(row.kantarId, row.tartiNo)))
  const byKantar = new Map<string, number[]>()
  for (const row of kantar) {
    if (row.surecDurumu === 'Sahada' || row.surecDurumu === 'Silindi') continue
    if (withLines.has(keyOf(row.kantarAdi, row.tartino))) continue
    const list = byKantar.get(row.kantarAdi) ?? []
    list.push(row.tartino)
    byKantar.set(row.kantarAdi, list)
  }
  for (const list of byKantar.values()) list.sort((a, b) => a - b)
  return new Map([...byKantar].sort(([a], [b]) => a.localeCompare(b)))
}

/**
 * Aralıktaki DİA satırlarının kantar sorgusunda olmayan tartıları: kantar -> sıralı tartı
 * no'lar. Kantar kaydı aralık dışındaysa kayıt kapsam dışıdır, yoksa H-B; ayırt etmek için
 * aranır. Tolerans için sorguya giren aralık öncesi satırlar aranmaz (kantarsızsa da
 * kapsam dışı). Tartı no'su ya da kantarı boş satır kantarda aranamaz; atlanır (yoksa
 * `"null"` integer filtresine gider ve Postgres sorguyu reddeder).
 */
export function missingKantarKeys(
  kantar: KantarRow[],
  dia: DiaRow[],
  from?: string,
  to?: string,
): Map<string, number[]> {
  const known = new Set(kantar.map((row) => `${row.kantarAdi}:${row.tartino}`))
  const byKantar = new Map<string, number[]>()
  for (const row of dia) {
    const day = row.tarih?.slice(0, 10)
    if (row.tartiNo === null || !row.kantarId) continue
    if (
      !day ||
      (from && day < from) ||
      (to && day > to) ||
      known.has(`${row.kantarId}:${row.tartiNo}`)
    )
      continue
    const list = byKantar.get(row.kantarId) ?? []
    if (!list.includes(row.tartiNo)) list.push(row.tartiNo)
    byKantar.set(row.kantarId, list)
  }
  for (const list of byKantar.values()) list.sort((a, b) => a - b)
  return new Map([...byKantar].sort(([a], [b]) => a.localeCompare(b)))
}

/**
 * Kapsam kantara göre (kullanıcı kararı, 2026-10-18): tarihi (çıkış günü) aralıktaki
 * kantar tartımları + girişi aralıktaki Sahada kayıtları. Kantar kaydı olmayan (ya da
 * günü bilinmeyen) tartı DİA gününe göre girer. Aralık dışı tartıma kesilen irsaliye o
 * tartının aralığında görünür, burada değil. `kantar` aralık dışı tartımları da içerebilir
 * (bkz. `missingKantarKeys`), `dia` aralıktan önceki toleransı ve sonrasını da.
 *
 * Sahada (çıkışı yok) girişe göre kapsama girer (kullanıcı kararı, 2026-10-18): önceden
 * her aralıkta listeleniyordu ve haftalık bekleyen araçlar her günün hata sayısına
 * ekleniyordu. Girişi bilinmeyen Sahada kaydı yerleştirilemez; her aralıkta kalır.
 */
export function reconcile(input: {
  kantar: KantarRow[]
  dia: DiaRow[]
  /** Kantarın DİA aktarım sonuçları; yoksa yalnız süre kuralı uygulanır. */
  aktarim?: AktarimRow[]
  /** Sahada araçların sonraki tartıları (`reweighCandidates`); `kantar`dakilerle birlikte aranır. */
  reweighs?: KantarRow[]
  from?: string
  to?: string
  /** Şimdiki İstanbul duvar saati, `wallClockMs` biçiminde. */
  nowMs: number
}): ReconcileResult {
  const { from, to, nowMs } = input
  const kantarByKey = new Map(input.kantar.map((row) => [keyOf(row.kantarAdi, row.tartino), row]))
  const byPlate = new Map<string, KantarRow[]>()
  for (const row of [...input.kantar, ...(input.reweighs ?? [])]) {
    const plate = plateKey(row.plaka)
    if (!plate) continue
    const list = byPlate.get(`${row.kantarAdi}|${plate}`)
    if (list) list.push(row)
    else byPlate.set(`${row.kantarAdi}|${plate}`, [row])
  }
  const aktarimByKey = new Map(
    (input.aktarim ?? []).map((row) => [keyOf(row.kantarAdi, row.tartino), row]),
  )
  const linesByKey = new Map<string, DiaRow[]>()
  for (const row of input.dia) {
    const key = keyOf(row.kantarId, row.tartiNo)
    const lines = linesByKey.get(key)
    if (lines) lines.push(row)
    else linesByKey.set(key, [row])
  }

  const keys = new Set<string>()
  for (const key of new Set([...kantarByKey.keys(), ...linesByKey.keys()])) {
    const row = kantarByKey.get(key)
    const onsite = row?.surecDurumu === 'Sahada'
    // Sahada (çıkışı yok) kayıt giriş gününe göre; girişi bilinmeyen her aralıkta kalır.
    const kantarDay = row && (onsite ? row.giris : row.tarih)
    const inScope = kantarDay
      ? inRange(kantarDay, from, to)
      : (row !== undefined && onsite) ||
        (linesByKey.get(key) ?? []).some((line) => inRange(line.tarih, from, to))
    if (inScope) keys.add(key)
  }

  // Aynı irsaliye birden fazla tartıda (Mal Alım satırlarında irsaliye no boş).
  const tartisByIrsaliye = new Map<string, Set<string>>()
  for (const key of keys) {
    for (const line of linesByKey.get(key) ?? []) {
      if (!text(line.irsaliyeNo).trim()) continue
      const set = tartisByIrsaliye.get(line.irsaliyeNo!) ?? new Set()
      set.add(key)
      tartisByIrsaliye.set(line.irsaliyeNo!, set)
    }
  }

  const records: ReconcileRecord[] = []
  const prefixMismatches: PrefixMismatch[] = []
  const unknownPrefixes = new Map<string, Set<string>>()

  for (const key of keys) {
    const kantar = kantarByKey.get(key)
    const lines = linesByKey.get(key) ?? []
    const mlzm = lines.filter((line) => line.kalemTuru === 'MLZM')
    const first = mlzm[0] ?? lines[0]
    const kantarAdi = kantar?.kantarAdi ?? first!.kantarId
    const tartino = kantar?.tartino ?? first!.tartiNo
    const issues: Issue[] = []

    for (const line of lines) {
      const expected = EXPECTED_PREFIX[kantarAdi]
      const actual = `${text(line.tartiOnek)} / ${line.firmaKodu ?? ''}`
      if (!expected) {
        const set = unknownPrefixes.get(kantarAdi) ?? new Set()
        set.add(actual)
        unknownPrefixes.set(kantarAdi, set)
      } else if (line.tartiOnek !== expected.prefix || line.firmaKodu !== expected.firm) {
        prefixMismatches.push({
          kantarAdi,
          tartino,
          expected: `${expected.prefix} / ${expected.firm}`,
          actual,
        })
      }
    }

    const giris = kantar?.giris ?? null
    const girisMs = wallClockMs(giris)
    const staleOnsite = staleOnsiteIssue(kantar, girisMs, nowMs, byPlate)

    let status: RecordStatus
    if (!kantar) {
      issues.push({ code: 'H-B', kantar: 'yok', dia: `${lines.length} satır` })
      status = 'error'
    } else if (kantar.surecDurumu === 'Sahada' && lines.length === 0) {
      if (staleOnsite) issues.push(staleOnsite)
      status = staleOnsite ? 'error' : 'onsite'
    } else if (kantar.surecDurumu === 'Silindi' && lines.length === 0) {
      // Silinmiş tartıya irsaliye kesilmemesi doğru; ayrı durumda listelenir (kullanıcı
      // kararı, 2026-10-19; önceden kapsam dışıydı ve Canlı Tartım'la sayı tutmuyordu).
      status = 'deleted'
    } else if (kantar.surecDurumu !== 'Tamamlandı' && lines.length > 0) {
      issues.push({
        code: 'H-B',
        note: `Kantarda ${text(kantar.surecDurumu)}`,
        kantar: text(kantar.surecDurumu),
        dia: `${lines.length} satır`,
      })
      if (staleOnsite) issues.push(staleOnsite)
      status = 'error'
    } else if (lines.length === 0) {
      // Belirsiz vb. de net tonajla çıkmış sayılır: irsaliyesiz ise H-A (kullanıcı kararı,
      // 2026-10-19). Kantarın DİA aktarım kaydı varsa (kullanıcı kararı, 2026-10-21):
      // başarısızsa beklenmeden H-A ve DİA'nın hata mesajı; aktarıldıysa süre aynı, H-A'nın
      // notu senkron sorununu gösterir.
      const cikis = wallClockMs(kantar.cikis)
      const elapsed = cikis === null ? null : nowMs - cikis
      const aktarim = aktarimByKey.get(key)
      const hata =
        aktarim?.aktarildi === 1 ? null : aktarimHataMesaji(aktarim?.aktarimHatasi ?? null)
      const aktarildi = aktarim?.aktarildi === 1
      if (!hata && elapsed !== null && elapsed < IRSALIYE_WAIT_MS) {
        status = 'waiting'
      } else {
        const surec = text(kantar.surecDurumu)
        const note = hata
          ? `DİA aktarım hatası: ${hata}`
          : aktarildi
            ? "DİA'ya aktarıldı ama DİA'da bulunamadı"
            : surec !== 'Tamamlandı'
              ? `Kantarda ${surec}`
              : undefined
        issues.push({
          code: 'H-A',
          ...(note && { note }),
          kantar: surec,
          dia: 'yok',
        })
        status = 'error'
      }
    } else {
      if (mlzm.length === 0) {
        issues.push({
          code: 'H-A',
          note: 'Malzeme satırı yok',
          kantar: 'Tamamlandı',
          dia: 'MLZM yok',
        })
      } else {
        issues.push(...compareMlzm(kantar, mlzm[0]!))
      }
      if (mlzm.length > 1) {
        issues.push({
          code: 'H-K',
          note: 'Birden fazla MLZM satırı',
          kantar: '1 satır',
          dia: `${mlzm.length} satır`,
        })
      }
      issues.push(...transportIssues(kantar, lines, toKg(mlzm[0]?.miktar ?? null)))
      status = statusOf(issues)
    }

    for (const irsaliye of new Set(lines.map((line) => line.irsaliyeNo))) {
      const tartis = irsaliye ? tartisByIrsaliye.get(irsaliye) : undefined
      if (tartis && tartis.size > 1) {
        issues.push({
          code: 'H-K',
          note: 'Aynı irsaliye birden fazla tartıda',
          dia: irsaliye!,
          diff: `${tartis.size} tartı`,
        })
        status = 'error'
      }
    }

    // Silinmiş tartının neti tonaja girmez.
    const kantarNet = kantar && status !== 'deleted' ? kantarKg(kantar) : null
    const diaNet = toKg(mlzm[0]?.miktar ?? null)
    records.push({
      key,
      kantarAdi,
      tartino,
      plaka: kantar?.plaka ?? first?.plaka ?? null,
      cari: kantar?.cariAdi ?? first?.cariUnvan ?? null,
      tarih: kantar?.tarih ?? first?.tarih ?? null,
      kantarTon: kantarNet === null ? null : kantarNet / 1000,
      diaTon: diaNet === null ? null : diaNet / 1000,
      status,
      issues,
      giris,
      onsiteMs: status === 'onsite' && girisMs !== null ? Math.max(0, nowMs - girisMs) : null,
      kantar: kantar ?? null,
      lines,
    })
  }

  records.sort(
    (a, b) =>
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      compareTr(a.kantarAdi, b.kantarAdi) ||
      b.tartino - a.tartino,
  )
  return {
    records,
    prefixMismatches,
    unknownPrefixes: [...unknownPrefixes].map(([kantarAdi, values]) => ({
      kantarAdi,
      values: [...values].sort(),
    })),
  }
}

export interface PageScope {
  /** Boş = tüm kantarlar. */
  kantars: string[]
  /** Gün aralığı (`YYYY-MM-DD`); saat yalnız ilgili gün de varsa uygulanır. */
  from?: string
  to?: string
  /** `SS:DD`. */
  timeFrom?: string
  timeTo?: string
}

/**
 * Sayfa kapsamı (kantar + saat) mutabakat sonucuna uygulanır.
 *
 * Saat, tamamlanan tartımın **çıkış** zamanına uygulanır: net tonaj ve irsaliye
 * yükümlülüğü çıkışta doğar (bekleme kuralı da çıkışa bakar) ve kaynakta `tarih` çıkış
 * günüdür (canlıda 47.016 tamamlanmış tartımın hepsinde). Pencere sürekli bir aralıktır
 * (`from` + `timeFrom` .. `to` + `timeTo`, ör. gece vardiyası 16.10 22:00 – 17.10 06:00).
 * Çıkışı olmayan tartı (Sahada, H-L) giriş zamanına göre süzülür (kullanıcı kararı,
 * 2026-10-17): yoksa haftalardır sahada bekleyen araçlar her vardiya görünümüne
 * giriyordu. Kantar kaydı olmayan DİA satırının saati yok; gün kuralıyla kapsamda kalır.
 */
export function scopeResult(result: ReconcileResult, scope: PageScope): ReconcileResult {
  let fromMs =
    scope.from && isTimeOfDay(scope.timeFrom)
      ? wallClockMs(`${scope.from}T${scope.timeFrom}:00`)
      : null
  let toMs =
    scope.to && isTimeOfDay(scope.timeTo) ? wallClockMs(`${scope.to}T${scope.timeTo}:59`) : null
  // Ters pencere (başlangıç bitişten sonra) uygulanmaz: ekran hatayı gösterir, veri boşalmaz.
  const reversed = fromMs !== null && toMs !== null && fromMs > toMs
  if (reversed) {
    fromMs = null
    toMs = null
  }
  const kantarOk = (kantarAdi: string) =>
    scope.kantars.length === 0 || scope.kantars.includes(kantarAdi)
  if (scope.kantars.length === 0 && fromMs === null && toMs === null) return result

  const records = result.records.filter((record) => {
    if (!kantarOk(record.kantarAdi)) return false
    const at = wallClockMs(record.kantar?.cikis ?? record.giris)
    if (at === null) return true
    return (fromMs === null || at >= fromMs) && (toMs === null || at <= toMs)
  })
  const kept = new Set(records.map((record) => keyOf(record.kantarAdi, record.tartino)))
  return {
    records,
    prefixMismatches: result.prefixMismatches.filter((item) =>
      kept.has(keyOf(item.kantarAdi, item.tartino)),
    ),
    unknownPrefixes: result.unknownPrefixes.filter((item) => kantarOk(item.kantarAdi)),
  }
}

/** Kayıttaki kodlar, tekrarsız ve sabit sırada. */
export function issueCodes(record: ReconcileRecord): IssueCode[] {
  const codes = new Set(record.issues.map((issue) => issue.code))
  return ISSUE_CODES.filter((code) => codes.has(code))
}

/** Ekrandaki durum sırası (önce sorunlular). */
export const RECORD_STATUSES = (Object.keys(STATUS_RANK) as RecordStatus[]).sort(
  (a, b) => STATUS_RANK[a] - STATUS_RANK[b],
)

/** Kayıt başına ton -> tam kg; toplamlarda kayan nokta hatası birikmesin. */
const recordKg = (ton: number | null) => (ton === null ? null : Math.round(ton * 1000))

/** DİA MLZM miktarı - kantar neti (kg); taraflardan biri yoksa `null`. */
export function diffKg(record: ReconcileRecord): number | null {
  const kantar = recordKg(record.kantarTon)
  const dia = recordKg(record.diaTon)
  return kantar === null || dia === null ? null : dia - kantar
}

export const TONNAGE_GROUPS = ['matched', 'kantarOnly', 'diaOnly'] as const
export type TonnageGroupKey = (typeof TONNAGE_GROUPS)[number]

/** Tonaj grubu: tartı sayısı ve iki taraftaki toplam (kg). */
export interface TonnageGroup {
  count: number
  kantarKg: number
  diaKg: number
}

/**
 * Toplam fark (DİA - kantar) üç gruba ayrılır ve toplamı tam verir:
 * eşleşen tartılardaki fark + yalnız DİA'dakiler - yalnız kantardakiler.
 */
export interface TonnageSummary extends Record<TonnageGroupKey, TonnageGroup> {
  kantarKg: number
  kantarCount: number
  diaKg: number
  diaCount: number
  /** `diaKg - kantarKg`. */
  diffKg: number
  /** Kantar neti de DİA MLZM miktarı da olan tartılar. */
  matched: TonnageGroup
  /** Kantar neti var, DİA MLZM miktarı yok (irsaliye bekleniyor / H-A). */
  kantarOnly: TonnageGroup
  /** DİA MLZM miktarı var, kantar neti yok (H-B ya da tartım tamamlanmamış). */
  diaOnly: TonnageGroup
}

const emptyGroup = (): TonnageGroup => ({ count: 0, kantarKg: 0, diaKg: 0 })

export function summarizeTonnage(records: ReconcileRecord[]): TonnageSummary {
  const matched = emptyGroup()
  const kantarOnly = emptyGroup()
  const diaOnly = emptyGroup()
  for (const record of records) {
    const kantar = recordKg(record.kantarTon)
    const dia = recordKg(record.diaTon)
    const group =
      kantar !== null && dia !== null
        ? matched
        : kantar !== null
          ? kantarOnly
          : dia !== null
            ? diaOnly
            : null
    if (!group) continue
    group.count += 1
    group.kantarKg += kantar ?? 0
    group.diaKg += dia ?? 0
  }
  const kantarKg = matched.kantarKg + kantarOnly.kantarKg
  const diaKg = matched.diaKg + diaOnly.diaKg
  return {
    kantarKg,
    kantarCount: matched.count + kantarOnly.count,
    diaKg,
    diaCount: matched.count + diaOnly.count,
    diffKg: diaKg - kantarKg,
    matched,
    kantarOnly,
    diaOnly,
  }
}

export interface KantarSummary {
  kantarAdi: string
  total: number
  byStatus: Record<RecordStatus, number>
  tonnage: TonnageSummary
}

export function summarizeByKantar(records: ReconcileRecord[]): KantarSummary[] {
  const groups = new Map<string, ReconcileRecord[]>()
  for (const record of records) {
    const items = groups.get(record.kantarAdi)
    if (items) items.push(record)
    else groups.set(record.kantarAdi, [record])
  }
  return [...groups]
    .sort(([a], [b]) => compareTr(a, b))
    .map(([kantarAdi, items]) => ({
      kantarAdi,
      total: items.length,
      byStatus: Object.fromEntries(
        RECORD_STATUSES.map((status) => [
          status,
          items.filter((item) => item.status === status).length,
        ]),
      ) as Record<RecordStatus, number>,
      tonnage: summarizeTonnage(items),
    }))
}

export interface RecordFilter {
  statuses: RecordStatus[]
  kantars: string[]
  codes: IssueCode[]
  /** Tartı no, plaka ya da cari içinde geçen metin. */
  search: string
}

export function filterRecords(records: ReconcileRecord[], filter: RecordFilter) {
  const plate = normalizePlate(filter.search)
  const name = normalizeName(filter.search)
  return records.filter(
    (record) =>
      (filter.statuses.length === 0 || filter.statuses.includes(record.status)) &&
      (filter.kantars.length === 0 || filter.kantars.includes(record.kantarAdi)) &&
      (filter.codes.length === 0 ||
        record.issues.some((issue) => filter.codes.includes(issue.code))) &&
      (!name ||
        String(record.tartino).includes(name) ||
        normalizePlate(record.plaka).includes(plate) ||
        normalizeName(record.cari).includes(name)),
  )
}

const SORT_VALUE = {
  status: (record) => STATUS_RANK[record.status],
  kantar: (record) => record.kantarAdi,
  tartino: (record) => record.tartino,
  tarih: (record) => day(record.tarih),
  // Offset'siz İstanbul duvar saati dizeleri metin olarak doğru sıralanır.
  giris: (record) => record.giris,
  cikis: (record) => record.kantar?.cikis ?? null,
  plaka: (record) => normalizePlate(record.plaka) || null,
  cari: (record) => record.cari,
  note: (record) => record.override?.note ?? null,
  kantarTon: (record) => record.kantarTon,
  diaTon: (record) => record.diaTon,
  diff: diffKg,
} satisfies Record<string, (record: ReconcileRecord) => string | number | null>

export type RecordSortKey = keyof typeof SORT_VALUE

export const isRecordSortKey = (id: string): id is RecordSortKey => id in SORT_VALUE

/** Çok kolonlu sıralama; boş değerler yönden bağımsız en sonda. */
export function sortRecords(
  records: ReconcileRecord[],
  sorting: { id: string; desc: boolean }[],
): ReconcileRecord[] {
  const keys = sorting.filter((item) => isRecordSortKey(item.id))
  if (keys.length === 0) return records
  return [...records].sort((a, b) => {
    for (const { id, desc } of keys) {
      const value = SORT_VALUE[id as RecordSortKey]
      const x = value(a)
      const y = value(b)
      if (x === y) continue
      if (x === null) return 1
      if (y === null) return -1
      const order =
        typeof x === 'number' && typeof y === 'number' ? x - y : compareTr(String(x), String(y))
      if (order !== 0) return desc ? -order : order
    }
    return 0
  })
}

/** Şimdiki İstanbul duvar saati (ms, `wallClockMs` ile aynı eksende). */
export function istanbulNowMs(now = new Date()): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Istanbul',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(now)
      .map((part) => [part.type, part.value]),
  )
  return wallClockMs(
    `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`,
  )!
}

/**
 * Elle düzeltmeleri kayıtlara uygular (kullanıcı kararı, 2026-10-22). Durum verilmişse
 * kaydın durumu onunla değişir; kuralların bulduğu durum ve hata kodları korunur (detayda
 * görünür). Kaynak verisi değişmez; düzeltmeler bizim veritabanımızda.
 */
export function applyOverrides(result: ReconcileResult, overrides: OverrideRow[]): ReconcileResult {
  if (overrides.length === 0) return result
  const byKey = new Map(overrides.map((row) => [keyOf(row.kantarAdi, row.tartino), row]))
  let changed = false
  const records = result.records.map((record) => {
    const row = byKey.get(record.key)
    if (!row) return record
    changed = true
    const override: RecordOverride = {
      status: row.status,
      note: row.note,
      updatedByName: row.updatedByName,
      updatedAt: row.updatedAt,
    }
    return {
      ...record,
      override,
      computedStatus: record.status,
      status: row.status ?? record.status,
    }
  })
  return changed ? { ...result, records } : result
}
